// Shared Board/request metadata — the single place that knows how app_request
// kinds and statuses are presented. Lifted out of Apps.jsx (REQUEST_KIND_META)
// and MyRequestsPanel.jsx (STATUS_META) when the Board became a kanban page,
// so the Board, the Apps dialogs, and any future surface stay in sync.
import {
  Bot,
  Brain,
  Bug,
  CheckCircle2,
  CircleDashed,
  Clock,
  FileText,
  GraduationCap,
  Hand,
  Loader2,
  MessageSquare,
  Plug,
  Route,
  Search,
  Sparkles,
  Workflow,
  Wrench,
  XCircle,
} from "lucide-react";

// `tenancyChoice` — the dialog asks up front whether the delivered app is a
// shared workspace or multi-tenant (each user sees only their own data); only
// kinds that deliver a user-facing app carry it. `suggestFeatures` — the
// dialog offers a generated feature checklist (the form twin of the chat's
// propose_features widget); mirrors FEATURE_KIND_BASELINES on the backend.
export const REQUEST_KIND_META = {
  new_app: { label: "New app", icon: Sparkles, hint: "Describe the app you need — an agent will build it and you'll own it", tenancyChoice: true, suggestFeatures: true },
  change: { label: "Change an app", icon: Wrench, hint: "Build a change into an existing app" },
  bug_fix: { label: "Bug fix", icon: Bug, hint: "Something in an app is broken" },
  new_agent: { label: "New agent", icon: Bot, hint: "A standalone project agent built from a plain-language brief" },
  change_agent: { label: "Change an agent", icon: Wrench, hint: "A change to an existing agent" },
  // Filed from the Projects page "Project assistant" option.
  new_project: { label: "New project", icon: Sparkles, hint: "A complete project built by the project assistant", tenancyChoice: true, suggestFeatures: true },
  change_project: { label: "Change a project", icon: Wrench, hint: "A change spanning the whole project" },
  // Never filed from a dialog — a chat delegate promoted to a background run
  // when its answer needs longer than the soft timeout (see
  // assistant/delegation-mcp.js). Listed so Board rows carry a proper label.
  investigation: { label: "Investigation", icon: Search, hint: "A long-running agent investigation promoted from chat" },
  // Filed from the Workflows rail "Build a workflow" button.
  new_workflow: { label: "New workflow", icon: Workflow, hint: "A business process built by an agent", suggestFeatures: true },
  change_workflow: { label: "Change a workflow", icon: Wrench, hint: "A change to an existing process" },
  // Filed from the Pipeline rail "Build a pipeline" button.
  new_pipeline: { label: "New pipeline", icon: Route, hint: "A governed data pipeline built by an agent", suggestFeatures: true },
  change_pipeline: { label: "Change a pipeline", icon: Wrench, hint: "A change to an existing pipeline" },
  // Filed from the Models rail "Build a model" button.
  new_model: { label: "New model", icon: Brain, hint: "A custom model trained on this project's governed data" },
  change_model: { label: "Change a model", icon: Wrench, hint: "A change to an existing model — retrain, new features, a different target" },
  // Filed from the Reports rail "New report" button (or the ✨ action on a row).
  new_report: { label: "New report", icon: FileText, hint: "A pixel-perfect document built by an agent from this project's governed data" },
  change_report: { label: "Change a report", icon: Wrench, hint: "A change to an existing report — new layout, different data, extra parameters" },
  // Filed from the Data Sources rail "Build a source" button. Delivers a
  // reusable connector TYPE (a source_kind row + its config page), not a single
  // connection — so the next person connecting the same system just fills a form.
  new_source: { label: "New data source", icon: Plug, hint: "A connector to a system cortex can't reach yet — an agent researches it and builds the connection form" },
  change_source: { label: "Change a data source", icon: Wrench, hint: "A change to an existing connector — new fields, different endpoint, fixed authentication" },
  // Filed from the Guides page "Build a guide" button (or the ✨ action on a row).
  new_guide: { label: "New guide", icon: GraduationCap, hint: "An in-product walkthrough (spotlight tour) built by an agent for this project" },
  change_guide: { label: "Change a guide", icon: Wrench, hint: "A change to an existing guide — different steps, updated copy, new screens" },
  // Untyped capture (Feed → To Do). Never offered by the New-task dialog; a
  // human sets the real kind on the Board (Edit) before it may queue — the
  // backend refuses to queue a triage row.
  triage: { label: "Task", icon: CircleDashed, hint: "An untyped capture — set its type before taking it into work" },
};

// Status → chip presentation. Reuses the alert severity CSS vars so the chip
// colours match the rest of the product in both themes.
export const STATUS_META = {
  todo:      { label: "To Do",     icon: CircleDashed, cls: "text-[var(--page-muted,#6b7280)] bg-[var(--table-stripe,#f3f4f6)]" },
  queued:    { label: "Queued",    icon: Clock,        cls: "text-[var(--severity-info-text,#1e40af)] bg-[var(--severity-info-bg,#dbeafe)]" },
  claimed:   { label: "Claimed",   icon: Hand,         cls: "text-[var(--severity-info-text,#1e40af)] bg-[var(--severity-info-bg,#dbeafe)]" },
  running:   { label: "Running",   icon: Loader2,      cls: "text-[var(--severity-warning-text,#92400e)] bg-[var(--severity-warning-bg,#fef3c7)]", spin: true },
  // The executor asked a question and is blocked on the requester's reply
  // (worker block verb). Answer in the card's comments, then requeue.
  waiting:   { label: "Waiting",   icon: MessageSquare, cls: "text-[var(--severity-warning-text,#92400e)] bg-[var(--severity-warning-bg,#fef3c7)]" },
  done:      { label: "Done",      icon: CheckCircle2, cls: "text-[var(--severity-success-text,#166534)] bg-[var(--severity-success-bg,#dcfce7)]" },
  failed:    { label: "Failed",    icon: XCircle,      cls: "text-[var(--severity-critical-text,#b91c1c)] bg-[var(--severity-critical-bg,#fee2e2)]" },
  cancelled: { label: "Cancelled", icon: XCircle,      cls: "text-[var(--page-muted,#6b7280)] bg-[var(--table-stripe,#f3f4f6)]" },
};

export const OPEN_STATUSES = new Set(["queued", "claimed", "running", "waiting"]);
export const TERMINAL_STATUSES = new Set(["done", "failed", "cancelled"]);
// A claimed/running row this old (30 min) with no activity is treated as stale —
// the requester gets a self-service Cancel action (jobs orphaned by a worker
// or pod restart). Mirrors STALE_CANCEL_MS in backend/modules/apps.
export const STALE_CANCEL_MS = 30 * 60 * 1000;

// The kanban columns. `statuses` are the DB statuses a column displays —
// 'claimed' has no column of its own (it is the seconds-long claim handshake
// of the worker contract) and folds into Running. `drop` is the status a
// card drop sets, null for columns the user can't drop into.
export const BOARD_COLUMNS = [
  { key: "todo", title: "To Do", statuses: ["todo"], drop: "todo" },
  { key: "queued", title: "Queued", statuses: ["queued"], drop: "queued" },
  { key: "running", title: "Running", statuses: ["claimed", "running"], drop: null },
  { key: "waiting", title: "Waiting", statuses: ["waiting"], drop: null },
  { key: "done", title: "Done", statuses: ["done"], drop: "done" },
  // Failed and cancelled fold into ONE column: identical move semantics
  // (only → queued: retry / reopen), and cancelled alone was the second-
  // largest, least-valuable column on real boards. Cards inside carry a
  // status chip to tell the two apart.
  { key: "stopped", title: "Stopped", statuses: ["failed", "cancelled"], drop: null },
];

// Legal manual moves — MUST mirror BOARD_MOVES in backend/modules/apps.
// todo ⇄ queued (park / take into work), todo|failed → done (close out a card
// whose work happened outside the fleet queue), failed|cancelled → queued
// (retry / reopen), waiting → queued (answered the executor's question —
// resume). In-flight cards are never user-movable, and done is final.
export const BOARD_MOVES = {
  todo: new Set(["queued", "done"]),
  queued: new Set(["todo"]),
  failed: new Set(["queued", "done"]),
  cancelled: new Set(["queued"]),
  waiting: new Set(["queued"]),
};

export function timeAgo(ts) {
  if (!ts) return "";
  const d = new Date(ts);
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return d.toLocaleDateString();
}

// Wall-clock stamp for Activity log lines ("20:09:24", or "12/08 20:09:24"
// once the entry is from another day). Distinct from timeAgo, which collapses
// to a bare date after 24h — useless for a log, where the pacing BETWEEN
// entries (5 seconds apart or 10 minutes?) is the whole signal.
export function logStamp(ts) {
  if (!ts) return "";
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return "";
  const p = (n) => String(n).padStart(2, "0");
  const hms = `${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
  return d.toDateString() === new Date().toDateString()
    ? hms
    : `${p(d.getDate())}/${p(d.getMonth() + 1)} ${hms}`;
}

// Compact elapsed duration ("48s" / "14m" / "1h 20m") for the live progress
// line. Distinct from timeAgo, which phrases a PAST event ("3m ago"); this
// phrases an ONGOING one, so it stays second-resolution under a minute.
export function elapsed(ts) {
  if (!ts) return "";
  const s = Math.floor((Date.now() - new Date(ts).getTime()) / 1000);
  if (s < 60) return `${Math.max(s, 0)}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return m ? `${h}h ${m}m` : `${h}h`;
}

// Why a queued build is waiting. Heavy builds run ONE AT A TIME on the agent
// fleet, so "#1 in queue" routinely means a 10-20 min wait behind another
// build rather than a stall.
//
// `orgInFlight` (from the API, org-scoped like the drainer) separates the two
// cases position alone cannot: waiting BEHIND a running build, versus nothing
// draining this queue at all. Claiming "waiting for the current build to
// finish" when no agent is attached reads as healthy-and-busy and sends people
// looking for a bug in the wrong place. Undefined → say nothing about agents.
export function queueHint(position, orgInFlight) {
  const idle = orgInFlight === false;
  if (!position) return idle ? "Waiting for a build agent" : "Waiting for a free build slot";
  if (position === 1) {
    return idle
      ? "Next up — waiting for a build agent to pick it up"
      : "Next up — waiting for the current build to finish";
  }
  const ahead = position - 1;
  const behind = `Waiting behind ${ahead} earlier build${ahead === 1 ? "" : "s"}`;
  return idle ? `${behind} — no build agent running` : behind;
}

// Compact token count for the agent-effort line: 853 → "853", 85300 → "85k",
// 1_240_000 → "1.2M".
export function fmtTokens(n) {
  if (!Number.isFinite(n) || n <= 0) return "0";
  if (n < 1000) return String(Math.round(n));
  if (n < 1000000) return `${Math.round(n / 1000)}k`;
  return `${(n / 1000000).toFixed(1)}M`;
}

// Compact duration from milliseconds: "48s", "3m 12s", "1h 04m".
export function fmtMs(ms) {
  if (!Number.isFinite(ms) || ms <= 0) return "";
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m ${String(s % 60).padStart(2, "0")}s`;
  return `${Math.floor(s / 3600)}h ${String(Math.floor((s % 3600) / 60)).padStart(2, "0")}m`;
}

// ── Shared form tokens ──────────────────────────────────────────────────────
// ONE visual language for every task window (Board's New task, the task
// modal, Feed's Add to Board). These lived as per-file copies and had already
// drifted (paddings, hovers, borders) — import from here, never re-declare.
export const btnPrimary = "inline-flex items-center gap-1.5 rounded-md bg-nav-active text-white text-sm px-3 py-1.5 border-0 cursor-pointer hover:bg-nav-active/90 active:bg-nav-active/80 transition-colors disabled:opacity-50 disabled:cursor-default";
export const btnGhost = "inline-flex items-center gap-1.5 rounded-md text-sm px-3 py-1.5 border border-card-border bg-transparent text-page-text cursor-pointer hover:bg-card-border/30 active:bg-card-border/50 transition-colors disabled:opacity-50 disabled:cursor-default";
export const fieldCls = "rounded-md border border-card-border bg-page-bg px-2.5 py-2 text-sm text-page-text outline-none focus:border-nav-active";
export const sectionLabel = "text-xs font-semibold text-[var(--page-muted,#6b7280)] uppercase tracking-wide";

export function isStale(r) {
  if (r.status !== "claimed" && r.status !== "running") return false;
  // Time since the LAST ACTIVITY, not since claim: updated_at moves on every
  // worker log append, so a long-but-active build (legit builds run 1-2h)
  // never reads as "stuck"; only a truly silent one does.
  const since = r.updated_at || r.claimed_at;
  return !!since && Date.now() - new Date(since).getTime() >= STALE_CANCEL_MS;
}
